import { useState, useEffect, useCallback } from 'react'
import { Shield, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Filter, X, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react'
import api, { auditApi, getErrorMessage } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'

// 操作動作顏色對照
const ACTION_COLORS = {
  create:     'bg-green-100 text-green-700',
  update:     'bg-blue-100 text-blue-700',
  delete:     'bg-red-100 text-red-700',
  analyze:    'bg-purple-100 text-purple-700',
  deduct:     'bg-orange-100 text-orange-700',
  cancel:     'bg-gray-100 text-gray-700',
  reset:      'bg-yellow-100 text-yellow-700',
  upload:     'bg-cyan-100 text-cyan-700',
  send_email: 'bg-indigo-100 text-indigo-700',
}

// 實體類型圖標
const ENTITY_ICONS = {
  budget: '💰',
  customer: '👤',
  expense_report: '📄',
}

export default function AuditLog() {
  const { user } = useAuth()
  const [logs, setLogs] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    customer_id: '',
    user_id: '',
    search: '',
  })
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    customers: [],
    entity_types: [],
    actions: [],
  })
  const [loading, setLoading] = useState(true)
  const [expandedRow, setExpandedRow] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const [page, setPage] = useState(1)
  const perPage = 30
  const [auditEnabled, setAuditEnabled] = useState(true)

  // 載入稽核日誌開關狀態
  useEffect(() => {
    api.get('/settings/audit-log')
      .then(res => { if (res.data.success) setAuditEnabled(res.data.enabled) })
      .catch(() => {})
  }, [])

  const toggleAuditEnabled = async () => {
    const action = auditEnabled ? '停用' : '啟用'
    const detail = auditEnabled
      ? '停用後系統將不再記錄操作日誌，但已有日誌不會刪除。'
      : '啟用後系統將自動記錄所有操作日誌。'
    if (!window.confirm(`確定要${action}稽核日誌嗎？\n\n${detail}`)) return
    try {
      const res = await api.post('/settings/audit-log', { enabled: !auditEnabled })
      if (res.data.success) setAuditEnabled(res.data.enabled)
    } catch (err) {
      console.error('切換稽核日誌失敗:', err)
    }
  }

  // 載入篩選選項
  useEffect(() => {
    auditApi.getFilters()
      .then(res => setFilterOptions(res.data))
      .catch(() => {})
  }, [])

  // 載入日誌
  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, per_page: perPage }
      if (filters.entity_type) params.entity_type = filters.entity_type
      if (filters.action) params.action = filters.action
      if (filters.customer_id) params.customer_id = filters.customer_id
      if (filters.user_id) params.user_id = filters.user_id
      if (filters.search) params.search = filters.search

      const res = await auditApi.getLogs(params)
      setLogs(res.data.data)
      setPagination(res.data.pagination)
    } catch (err) {
      console.error('載入稽核日誌失敗:', getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [page, filters])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  // 非管理員不顯示
  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center text-gray-500">
        <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>此頁面僅限管理員存取</p>
      </div>
    )
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const clearFilters = () => {
    setFilters({ entity_type: '', action: '', customer_id: '', user_id: '', search: '' })
    setPage(1)
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '')

  const formatDate = (iso) => {
    if (!iso) return '-'
    const d = new Date(iso)
    return d.toLocaleString('zh-TW', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-gray-700" />
          <h1 className="text-2xl font-bold text-gray-800">稽核日誌</h1>
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
            共 {pagination.total} 筆
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(prev => !prev)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            篩選
            {hasActiveFilters && (
              <span className="ml-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {Object.values(filters).filter(v => v !== '').length}
              </span>
            )}
          </button>
          <button
            onClick={fetchLogs}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            重新整理
          </button>
          {/* 稽核日誌開關 */}
          <button
            onClick={toggleAuditEnabled}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${
              auditEnabled
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-gray-50 border-gray-300 text-gray-500'
            }`}
            title={auditEnabled ? '稽核日誌已啟用，點擊停用' : '稽核日誌已停用，點擊啟用'}
          >
            {auditEnabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
            {auditEnabled ? '記錄中' : '已停用'}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-4 p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {/* 搜尋 */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜尋描述、名稱..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            {/* 實體類型 */}
            <select
              value={filters.entity_type}
              onChange={e => handleFilterChange('entity_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">全部類型</option>
              {filterOptions.entity_types.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>

            {/* 動作 */}
            <select
              value={filters.action}
              onChange={e => handleFilterChange('action', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">全部動作</option>
              {filterOptions.actions.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>

            {/* 操作者 */}
            <select
              value={filters.user_id}
              onChange={e => handleFilterChange('user_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">全部操作者</option>
              {filterOptions.users.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>

            {/* 客戶 */}
            <select
              value={filters.customer_id}
              onChange={e => handleFilterChange('customer_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            >
              <option value="">全部客戶</option>
              {filterOptions.customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                清除全部篩選
              </button>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10"></th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">時間</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">操作者</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">動作</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">目標</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">客戶</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">描述</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    載入中...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <Shield className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    尚無稽核日誌紀錄
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <LogRow
                    key={log.id}
                    log={log}
                    expanded={expandedRow === log.id}
                    onToggle={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                    formatDate={formatDate}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              第 {pagination.page} / {pagination.pages} 頁（共 {pagination.total} 筆）
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!pagination.has_prev}
                className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                let pageNum
                if (pagination.pages <= 5) {
                  pageNum = i + 1
                } else if (page <= 3) {
                  pageNum = i + 1
                } else if (page >= pagination.pages - 2) {
                  pageNum = pagination.pages - 4 + i
                } else {
                  pageNum = page - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      pageNum === page
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-gray-200 text-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={!pagination.has_next}
                className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// 單筆日誌列
function LogRow({ log, expanded, onToggle, formatDate }) {
  const hasChanges = log.changes && Array.isArray(log.changes) && log.changes.length > 0

  return (
    <>
      <tr
        className={`hover:bg-gray-50 transition-colors ${expanded ? 'bg-blue-50/30' : ''} ${hasChanges ? 'cursor-pointer' : ''}`}
        onClick={hasChanges ? onToggle : undefined}
      >
        {/* Expand icon */}
        <td className="px-4 py-3">
          {hasChanges && (
            <span className="text-gray-400">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          )}
        </td>
        {/* 時間 */}
        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
          {formatDate(log.created_at)}
        </td>
        {/* 操作者 */}
        <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">
          {log.user_name}
        </td>
        {/* 動作 */}
        <td className="px-4 py-3 whitespace-nowrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600'}`}>
            {log.action_label}
          </span>
        </td>
        {/* 目標 */}
        <td className="px-4 py-3 text-sm text-gray-700">
          <span className="mr-1">{ENTITY_ICONS[log.entity_type] || '📋'}</span>
          <span className="text-gray-400 text-xs mr-1">{log.entity_type_label}</span>
          {log.entity_name && (
            <span className="font-medium">{log.entity_name}</span>
          )}
        </td>
        {/* 客戶 */}
        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
          {log.customer_name || '-'}
        </td>
        {/* 描述 */}
        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={log.description}>
          {log.description || '-'}
        </td>
        {/* IP */}
        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
          {log.ip_address || '-'}
        </td>
      </tr>

      {/* 展開的變更詳情 */}
      {expanded && hasChanges && (
        <tr className="bg-blue-50/40">
          <td colSpan={8} className="px-6 py-3">
            <div className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">變更詳情</div>
            <div className="grid gap-2 max-w-3xl">
              {log.changes.map((change, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-white rounded-lg px-3 py-2 border border-gray-100">
                  <span className="text-sm font-medium text-gray-700 min-w-[100px]">
                    {change.field_label || change.field}
                  </span>
                  <div className="flex items-center gap-2 text-sm flex-1 min-w-0">
                    {change.old != null ? (
                      <span className="bg-red-50 text-red-600 px-2 py-0.5 rounded font-mono text-xs break-all">
                        {String(change.old)}
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">（無）</span>
                    )}
                    <span className="text-gray-400">→</span>
                    {change.new != null ? (
                      <span className="bg-green-50 text-green-600 px-2 py-0.5 rounded font-mono text-xs break-all">
                        {String(change.new)}
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">（無）</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
